/**
 * Governance — AGM / EGM / committee / board meetings.
 *
 * Admin/secretary schedules meetings, attaches agenda items + proposals (each
 * with its own voting window + method). Residents vote from the mobile app;
 * admin sees live tallies + quorum on the drilldown.
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Vote,
  Plus,
  MoreHorizontal,
  Play,
  CheckCircle2,
  Ban,
  ListTree,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';

// ─── types ───
type MeetingKind = 'agm' | 'egm' | 'committee' | 'board' | 'informal';
type MeetingStatus = 'draft' | 'scheduled' | 'in_progress' | 'concluded' | 'cancelled';
type ProposalKind =
  | 'resolution'
  | 'motion'
  | 'election'
  | 'budget_approval'
  | 'policy_change'
  | 'appointment';
type VotingMethod = 'yes_no' | 'multi_choice' | 'approval';

interface Meeting {
  id: string;
  kind: MeetingKind;
  title: string;
  description: string | null;
  scheduled_at: string;
  duration_minutes: number;
  venue: string | null;
  is_virtual: boolean;
  meeting_url: string | null;
  status: MeetingStatus;
  quorum_percent: number;
  eligible_voter_count: number;
  voter_turnout: number;
  minutes: string | null;
  concluded_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
}

interface AgendaItem {
  id: string;
  meeting_id: string;
  ord: number;
  title: string;
  description: string | null;
  proposal_id: string | null;
}

interface Proposal {
  id: string;
  meeting_id: string | null;
  kind: ProposalKind;
  voting_method: VotingMethod;
  title: string;
  description: string | null;
  options: Array<{ id: string; label: string; votes?: number }>;
  voting_opens_at: string;
  voting_closes_at: string;
  quorum_percent: number;
  yes_count: number;
  no_count: number;
  abstain_count: number;
  total_votes: number;
  result_decided: boolean;
  result_status: string | null;
}

interface Summary {
  upcoming_meetings: number;
  active_proposals: number;
  votes_last_30d: number;
}

interface MeetingDetail {
  meeting: Meeting;
  agenda: AgendaItem[];
  proposals: Proposal[];
}

const KIND_LABEL: Record<MeetingKind, string> = {
  agm: 'AGM',
  egm: 'EGM',
  committee: 'Committee',
  board: 'Board',
  informal: 'Informal'
};
const PROPOSAL_KIND_LABEL: Record<ProposalKind, string> = {
  resolution: 'Resolution',
  motion: 'Motion',
  election: 'Election',
  budget_approval: 'Budget approval',
  policy_change: 'Policy change',
  appointment: 'Appointment'
};
const STATUS_VARIANT: Record<MeetingStatus, 'info' | 'ok' | 'warn' | 'alert' | 'muted'> = {
  draft: 'muted',
  scheduled: 'info',
  in_progress: 'warn',
  concluded: 'ok',
  cancelled: 'alert'
};

const FILTERS: ReadonlyArray<{ key: 'all' | MeetingStatus; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'concluded', label: 'Concluded' },
  { key: 'cancelled', label: 'Cancelled' }
];

// ─── form schemas ───
const MeetingForm = z.object({
  kind: z.enum(['agm', 'egm', 'committee', 'board', 'informal']),
  title: z.string().trim().min(2).max(200),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  scheduled_at: z.string().min(1, 'Date/time required'),
  duration_minutes: z.coerce.number().int().min(15).max(1440),
  venue: z.string().trim().max(200).optional().or(z.literal('')),
  is_virtual: z.boolean(),
  meeting_url: z.string().trim().max(400).optional().or(z.literal('')),
  quorum_percent: z.coerce.number().int().min(0).max(100)
});
type MeetingValues = z.infer<typeof MeetingForm>;
const MEETING_DEFAULTS: MeetingValues = {
  kind: 'committee',
  title: '',
  description: '',
  scheduled_at: '',
  duration_minutes: 60,
  venue: '',
  is_virtual: false,
  meeting_url: '',
  quorum_percent: 33
};

const ProposalForm = z.object({
  kind: z.enum([
    'resolution',
    'motion',
    'election',
    'budget_approval',
    'policy_change',
    'appointment'
  ]),
  voting_method: z.enum(['yes_no', 'multi_choice', 'approval']),
  title: z.string().trim().min(2).max(200),
  description: z.string().trim().max(3000).optional().or(z.literal('')),
  voting_opens_at: z.string().min(1),
  voting_closes_at: z.string().min(1),
  quorum_percent: z.coerce.number().int().min(0).max(100),
  options_raw: z.string().optional()
});
type ProposalValues = z.infer<typeof ProposalForm>;
const PROPOSAL_DEFAULTS: ProposalValues = {
  kind: 'resolution',
  voting_method: 'yes_no',
  title: '',
  description: '',
  voting_opens_at: '',
  voting_closes_at: '',
  quorum_percent: 33,
  options_raw: ''
};

const AgendaForm = z.object({
  title: z.string().trim().min(2).max(200),
  description: z.string().trim().max(1000).optional().or(z.literal('')),
  proposal_id: z.string().uuid().optional().or(z.literal('')),
  ord: z.coerce.number().int().min(0).max(10_000)
});
type AgendaValues = z.infer<typeof AgendaForm>;

function statusBadge(s: MeetingStatus): JSX.Element {
  return <Badge variant={STATUS_VARIANT[s]}>{s.replace('_', ' ')}</Badge>;
}

export function GovernancePage(): JSX.Element {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all');
  const [detailMeetingId, setDetailMeetingId] = useState<string | null>(null);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [agendaDialogMeeting, setAgendaDialogMeeting] = useState<string | null>(null);
  const [proposalDialogMeeting, setProposalDialogMeeting] = useState<string | null>(null);
  const [cancelMeetingId, setCancelMeetingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // ─── data ───
  const summaryQ = useQuery({
    queryKey: ['governance', 'summary'],
    queryFn: () => get<Summary>('/governance/summary')
  });
  const meetingsFiltersHash = useMemo(() => JSON.stringify([filter]), [filter]);
  const [meetingsCursor, setMeetingsCursor] = useState<string | null>(null);
  useEffect(() => {
    setMeetingsCursor(null);
  }, [meetingsFiltersHash]);
  const meetingsQ = useQuery({
    queryKey: ['governance', 'meetings', filter, meetingsCursor],
    queryFn: () => {
      const params: Record<string, string | number> = { limit: 50 };
      if (filter !== 'all') params.status = filter;
      if (meetingsCursor) params.cursor = meetingsCursor;
      return get<{ items: Meeting[]; next_cursor: string | null }>('/meetings', { params });
    }
  });
  const meetingsPaginator = useCursorAccumulator<Meeting>(meetingsQ.data, meetingsCursor, meetingsFiltersHash);
  const meetings = meetingsPaginator.items;

  const detailQ = useQuery({
    queryKey: ['governance', 'meeting', detailMeetingId],
    queryFn: () => get<MeetingDetail>(`/meetings/${detailMeetingId!}`),
    enabled: Boolean(detailMeetingId)
  });

  // ─── meeting create ───
  const meetingForm = useForm<MeetingValues>({
    resolver: zodResolver(MeetingForm),
    defaultValues: MEETING_DEFAULTS,
    mode: 'onChange'
  });

  const createMeeting = useMutation({
    mutationFn: (v: MeetingValues) =>
      post<Meeting>('/meetings', {
        kind: v.kind,
        title: v.title,
        description: v.description || undefined,
        scheduled_at: new Date(v.scheduled_at).toISOString(),
        duration_minutes: v.duration_minutes,
        venue: v.venue || undefined,
        is_virtual: v.is_virtual,
        meeting_url: v.meeting_url || undefined,
        quorum_percent: v.quorum_percent
      }),
    onSuccess: () => {
      toast.success('Meeting scheduled');
      qc.invalidateQueries({ queryKey: ['governance'] });
      setMeetingDialogOpen(false);
      meetingForm.reset(MEETING_DEFAULTS);
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  // ─── lifecycle actions ───
  const lifecycleMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'publish' | 'start' | 'conclude' }) =>
      post(`/meetings/${id}/${action}`, {}),
    onSuccess: (_d, v) => {
      toast.success(`Meeting ${v.action}d`);
      qc.invalidateQueries({ queryKey: ['governance'] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post(`/meetings/${id}/cancel`, { reason }),
    onSuccess: () => {
      toast.success('Meeting cancelled');
      qc.invalidateQueries({ queryKey: ['governance'] });
      setCancelMeetingId(null);
      setCancelReason('');
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  const closeProposal = useMutation({
    mutationFn: (id: string) => post(`/proposals/${id}/close`, {}),
    onSuccess: () => {
      toast.success('Proposal closed');
      qc.invalidateQueries({ queryKey: ['governance'] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  // ─── minutes save ───
  const [minutesDraft, setMinutesDraft] = useState<string>('');
  const minutesMut = useMutation({
    mutationFn: ({ id, minutes }: { id: string; minutes: string }) =>
      patch(`/meetings/${id}`, { minutes }),
    onSuccess: () => {
      toast.success('Minutes saved');
      qc.invalidateQueries({ queryKey: ['governance', 'meeting', detailMeetingId] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  // ─── agenda add ───
  const agendaForm = useForm<AgendaValues>({
    resolver: zodResolver(AgendaForm),
    defaultValues: { title: '', description: '', proposal_id: '', ord: 0 },
    mode: 'onChange'
  });
  const addAgenda = useMutation({
    mutationFn: (v: AgendaValues) =>
      post(`/meetings/${agendaDialogMeeting!}/agenda-items`, {
        title: v.title,
        description: v.description || undefined,
        proposal_id: v.proposal_id || undefined,
        ord: v.ord
      }),
    onSuccess: () => {
      toast.success('Agenda item added');
      qc.invalidateQueries({ queryKey: ['governance', 'meeting', agendaDialogMeeting] });
      setAgendaDialogMeeting(null);
      agendaForm.reset({ title: '', description: '', proposal_id: '', ord: 0 });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  const deleteAgenda = useMutation({
    mutationFn: (id: string) => del(`/agenda-items/${id}`),
    onSuccess: () => {
      toast.success('Agenda item removed');
      qc.invalidateQueries({ queryKey: ['governance', 'meeting', detailMeetingId] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  // ─── proposal add ───
  const proposalForm = useForm<ProposalValues>({
    resolver: zodResolver(ProposalForm),
    defaultValues: PROPOSAL_DEFAULTS,
    mode: 'onChange'
  });
  const addProposal = useMutation({
    mutationFn: (v: ProposalValues) => {
      const opts =
        v.voting_method !== 'yes_no'
          ? v.options_raw
              ?.split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
              .map((label, i) => ({ id: `opt_${i + 1}`, label })) ?? []
          : undefined;
      return post('/proposals', {
        meeting_id: proposalDialogMeeting,
        kind: v.kind,
        voting_method: v.voting_method,
        title: v.title,
        description: v.description || undefined,
        voting_opens_at: new Date(v.voting_opens_at).toISOString(),
        voting_closes_at: new Date(v.voting_closes_at).toISOString(),
        quorum_percent: v.quorum_percent,
        ...(opts && opts.length > 0 ? { options: opts } : {})
      });
    },
    onSuccess: () => {
      toast.success('Proposal added');
      qc.invalidateQueries({ queryKey: ['governance'] });
      setProposalDialogMeeting(null);
      proposalForm.reset(PROPOSAL_DEFAULTS);
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e))
  });

  // ─── action menu state ───
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  return (
    <PageHeader
      title="Governance"
      description="Schedule meetings, manage agenda items + proposals, capture voting results."
      actions={
        <Button onClick={() => setMeetingDialogOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Schedule meeting
        </Button>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
        <KpiCard label="Upcoming meetings" value={summaryQ.data?.upcoming_meetings ?? '—'} icon={<Vote className="h-5 w-5" />} />
        <KpiCard label="Active proposals" value={summaryQ.data?.active_proposals ?? '—'} icon={<Play className="h-5 w-5" />} />
        <KpiCard label="Votes (30 days)" value={summaryQ.data?.votes_last_30d ?? '—'} icon={<CheckCircle2 className="h-5 w-5" />} />
      </div>

      {/* filter pills */}
      <TabsPills
        className="mt-vt-6"
        value={filter}
        onChange={(v) => setFilter(v as 'all' | MeetingStatus)}
        options={FILTERS.map((f) => ({ value: f.key, label: f.label }))}
        aria-label="Meeting status filter"
      />

      <Card className="mt-vt-4">
        <CardContent className="p-0">
          {meetingsQ.isLoading ? (
            <div className="space-y-2 p-vt-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : meetings.length === 0 ? (
            <EmptyState
              icon={<Vote className="h-6 w-6" />}
              title="No meetings yet"
              description="Schedule an AGM, EGM, committee or board meeting to get started."
            />
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meeting</TableHead>
                  <TableHead>Scheduled at</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Eligible</TableHead>
                  <TableHead className="text-right">Turnout</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {meetings.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="max-w-[300px]">
                      <div className="font-medium text-vt-text">{m.title}</div>
                      <div className="text-vt-12 text-vt-text-muted">{KIND_LABEL[m.kind]} · {m.duration_minutes}m</div>
                    </TableCell>
                    <TableCell>{formatDateTime(m.scheduled_at)}</TableCell>
                    <TableCell>{statusBadge(m.status)}</TableCell>
                    <TableCell className="text-right">{m.eligible_voter_count || '—'}</TableCell>
                    <TableCell className="text-right">{m.voter_turnout || 0}</TableCell>
                    <TableCell className="relative">
                      <button
                        type="button"
                        className="rounded-vt-sm p-1 text-vt-text-muted hover:bg-vt-surface-2"
                        onClick={() => setOpenMenuId(openMenuId === m.id ? null : m.id)}
                        aria-label="Actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      {openMenuId === m.id ? (
                        <div
                          className="absolute right-2 top-9 z-20 w-52 rounded-vt-md border border-vt-border bg-vt-surface p-1 shadow-vt-2"
                          onMouseLeave={() => setOpenMenuId(null)}
                        >
                          <MenuItem
                            onClick={() => {
                              setDetailMeetingId(m.id);
                              setMinutesDraft(m.minutes ?? '');
                              setOpenMenuId(null);
                            }}
                          >
                            View detail
                          </MenuItem>
                          {m.status === 'draft' ? (
                            <>
                              <MenuItem onClick={() => { setAgendaDialogMeeting(m.id); setOpenMenuId(null); }}>Add agenda item</MenuItem>
                              <MenuItem onClick={() => { setProposalDialogMeeting(m.id); setOpenMenuId(null); }}>Add proposal</MenuItem>
                              <MenuItem onClick={() => { lifecycleMut.mutate({ id: m.id, action: 'publish' }); setOpenMenuId(null); }}>
                                Publish
                              </MenuItem>
                            </>
                          ) : null}
                          {m.status === 'scheduled' ? (
                            <MenuItem onClick={() => { lifecycleMut.mutate({ id: m.id, action: 'start' }); setOpenMenuId(null); }}>
                              Start
                            </MenuItem>
                          ) : null}
                          {(m.status === 'scheduled' || m.status === 'in_progress') ? (
                            <MenuItem onClick={() => { lifecycleMut.mutate({ id: m.id, action: 'conclude' }); setOpenMenuId(null); }}>
                              Conclude
                            </MenuItem>
                          ) : null}
                          {m.status !== 'concluded' && m.status !== 'cancelled' ? (
                            <MenuItem
                              onClick={() => { setCancelMeetingId(m.id); setOpenMenuId(null); }}
                              variant="danger"
                            >
                              Cancel
                            </MenuItem>
                          ) : null}
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <CursorPaginator
              hasMore={Boolean(meetingsPaginator.nextCursor)}
              loading={meetingsQ.isFetching}
              itemsShown={meetings.length}
              pageSize={50}
              onLoadMore={() => {
                if (meetingsPaginator.nextCursor) setMeetingsCursor(meetingsPaginator.nextCursor);
              }}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* ───── Create meeting dialog ───── */}
      <Dialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Schedule meeting</DialogTitle>
            <DialogDescription>
              Create a draft. Add agenda items and proposals before publishing.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={meetingForm.handleSubmit((v) => createMeeting.mutate(v))}
            className="grid grid-cols-1 gap-vt-3 md:grid-cols-2"
          >
            <div className="md:col-span-1">
              <Label>Kind</Label>
              <Select {...meetingForm.register('kind')}>
                <option value="agm">AGM</option>
                <option value="egm">EGM</option>
                <option value="committee">Committee</option>
                <option value="board">Board</option>
                <option value="informal">Informal</option>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label>Quorum %</Label>
              <Input type="number" min={0} max={100} {...meetingForm.register('quorum_percent')} />
            </div>
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input placeholder="Annual general meeting 2026" {...meetingForm.register('title')} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={3} {...meetingForm.register('description')} />
            </div>
            <div>
              <Label>Scheduled at</Label>
              <Input type="datetime-local" {...meetingForm.register('scheduled_at')} />
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input type="number" min={15} {...meetingForm.register('duration_minutes')} />
            </div>
            <div>
              <Label>Venue</Label>
              <Input placeholder="Clubhouse hall A" {...meetingForm.register('venue')} />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <input type="checkbox" {...meetingForm.register('is_virtual')} />
                Virtual meeting
              </Label>
              <Input className="mt-1" placeholder="https://meet.example.com/abc" {...meetingForm.register('meeting_url')} />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setMeetingDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMeeting.isPending}>
                {createMeeting.isPending ? 'Creating…' : 'Create draft'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ───── Add agenda dialog ───── */}
      <Dialog open={Boolean(agendaDialogMeeting)} onOpenChange={(v) => !v && setAgendaDialogMeeting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add agenda item</DialogTitle>
          </DialogHeader>
          <form onSubmit={agendaForm.handleSubmit((v) => addAgenda.mutate(v))} className="space-y-vt-3">
            <div>
              <Label>Title</Label>
              <Input {...agendaForm.register('title')} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={3} {...agendaForm.register('description')} />
            </div>
            <div className="grid grid-cols-2 gap-vt-3">
              <div>
                <Label>Order</Label>
                <Input type="number" min={0} {...agendaForm.register('ord')} />
              </div>
              <div>
                <Label>Linked proposal id (optional)</Label>
                <Input placeholder="uuid" {...agendaForm.register('proposal_id')} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAgendaDialogMeeting(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addAgenda.isPending}>Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ───── Add proposal dialog ───── */}
      <Dialog open={Boolean(proposalDialogMeeting)} onOpenChange={(v) => !v && setProposalDialogMeeting(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add proposal</DialogTitle>
            <DialogDescription>Proposals open + close at specific times. Residents vote during the window.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={proposalForm.handleSubmit((v) => addProposal.mutate(v))}
            className="grid grid-cols-1 gap-vt-3 md:grid-cols-2"
          >
            <div>
              <Label>Kind</Label>
              <Select {...proposalForm.register('kind')}>
                <option value="resolution">Resolution</option>
                <option value="motion">Motion</option>
                <option value="election">Election</option>
                <option value="budget_approval">Budget approval</option>
                <option value="policy_change">Policy change</option>
                <option value="appointment">Appointment</option>
              </Select>
            </div>
            <div>
              <Label>Voting method</Label>
              <Select {...proposalForm.register('voting_method')}>
                <option value="yes_no">Yes / No / Abstain</option>
                <option value="multi_choice">Multi-choice (pick 1)</option>
                <option value="approval">Approval (pick N)</option>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input {...proposalForm.register('title')} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={3} {...proposalForm.register('description')} />
            </div>
            <div>
              <Label>Voting opens at</Label>
              <Input type="datetime-local" {...proposalForm.register('voting_opens_at')} />
            </div>
            <div>
              <Label>Voting closes at</Label>
              <Input type="datetime-local" {...proposalForm.register('voting_closes_at')} />
            </div>
            <div>
              <Label>Quorum %</Label>
              <Input type="number" min={0} max={100} {...proposalForm.register('quorum_percent')} />
            </div>
            <div className="md:col-span-2">
              <Label>Options (one per line, for multi-choice / approval only)</Label>
              <Textarea rows={3} placeholder="Option A&#10;Option B&#10;Option C" {...proposalForm.register('options_raw')} />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setProposalDialogMeeting(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addProposal.isPending}>Add proposal</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ───── Cancel meeting dialog ───── */}
      <Dialog open={Boolean(cancelMeetingId)} onOpenChange={(v) => !v && setCancelMeetingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel meeting</DialogTitle>
          </DialogHeader>
          <Label>Reason</Label>
          <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelMeetingId(null)}>Back</Button>
            <Button
              variant="danger"
              disabled={!cancelReason.trim() || cancelMut.isPending}
              onClick={() => cancelMut.mutate({ id: cancelMeetingId!, reason: cancelReason.trim() })}
            >
              Confirm cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───── Detail dialog ───── */}
      <Dialog open={Boolean(detailMeetingId)} onOpenChange={(v) => !v && setDetailMeetingId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{detailQ.data?.meeting.title ?? 'Meeting'}</DialogTitle>
            <DialogDescription>
              {detailQ.data
                ? `${KIND_LABEL[detailQ.data.meeting.kind]} · ${formatDateTime(detailQ.data.meeting.scheduled_at)} · ${detailQ.data.meeting.status}`
                : 'Loading…'}
            </DialogDescription>
          </DialogHeader>
          {detailQ.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : detailQ.data ? (
            <div className="space-y-vt-4 max-h-[60vh] overflow-y-auto pr-1">
              {/* turnout */}
              <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-3 text-vt-13">
                Quorum {detailQ.data.meeting.quorum_percent}% · Eligible voters{' '}
                {detailQ.data.meeting.eligible_voter_count} · Turnout {detailQ.data.meeting.voter_turnout}
              </div>

              {/* agenda */}
              <section>
                <div className="mb-vt-2 flex items-center justify-between">
                  <h3 className="text-vt-14 font-semibold text-vt-text">
                    <ListTree className="mr-1 inline h-4 w-4" /> Agenda
                  </h3>
                  {detailQ.data.meeting.status === 'draft' ? (
                    <Button size="sm" variant="ghost" onClick={() => setAgendaDialogMeeting(detailQ.data!.meeting.id)}>
                      <Plus className="mr-1 h-3 w-3" /> Add
                    </Button>
                  ) : null}
                </div>
                {detailQ.data.agenda.length === 0 ? (
                  <p className="text-vt-12 text-vt-text-muted">No agenda items yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {detailQ.data.agenda.map((a) => (
                      <li key={a.id} className="flex items-start justify-between rounded-vt-sm border border-vt-border p-2 text-vt-13">
                        <div>
                          <div className="font-medium">{a.ord}. {a.title}</div>
                          {a.description ? <div className="text-vt-12 text-vt-text-muted">{a.description}</div> : null}
                        </div>
                        {detailQ.data!.meeting.status === 'draft' ? (
                          <button
                            type="button"
                            onClick={() => deleteAgenda.mutate(a.id)}
                            className="text-vt-text-muted hover:text-vt-danger"
                            aria-label="Delete agenda item"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* proposals */}
              <section>
                <div className="mb-vt-2 flex items-center justify-between">
                  <h3 className="text-vt-14 font-semibold text-vt-text">Proposals + tallies</h3>
                  {detailQ.data.meeting.status === 'draft' ? (
                    <Button size="sm" variant="ghost" onClick={() => setProposalDialogMeeting(detailQ.data!.meeting.id)}>
                      <Plus className="mr-1 h-3 w-3" /> Add
                    </Button>
                  ) : null}
                </div>
                {detailQ.data.proposals.length === 0 ? (
                  <p className="text-vt-12 text-vt-text-muted">No proposals attached.</p>
                ) : (
                  <ul className="space-y-2">
                    {detailQ.data.proposals.map((p) => (
                      <li key={p.id} className="rounded-vt-md border border-vt-border p-3 text-vt-13">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-medium text-vt-text">{p.title}</div>
                            <div className="text-vt-12 text-vt-text-muted">
                              {PROPOSAL_KIND_LABEL[p.kind]} · {p.voting_method.replace('_', ' ')}
                              {' · '} closes {formatDateTime(p.voting_closes_at)}
                            </div>
                          </div>
                          {p.result_decided ? (
                            <Badge variant="ok">{p.result_status}</Badge>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => closeProposal.mutate(p.id)}>
                              <Ban className="mr-1 h-3 w-3" /> Force close
                            </Button>
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-4 gap-2 text-center text-vt-12">
                          <Stat label="Yes" value={p.yes_count} />
                          <Stat label="No" value={p.no_count} />
                          <Stat label="Abstain" value={p.abstain_count} />
                          <Stat label="Total" value={p.total_votes} />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* minutes */}
              <section>
                <h3 className="mb-vt-2 text-vt-14 font-semibold text-vt-text">Minutes</h3>
                {detailQ.data.meeting.status === 'in_progress' || detailQ.data.meeting.status === 'concluded' ? (
                  <>
                    <Textarea
                      rows={6}
                      value={minutesDraft}
                      onChange={(e) => setMinutesDraft(e.target.value)}
                      placeholder="Capture key discussion points + decisions…"
                    />
                    <div className="mt-vt-2 flex justify-end">
                      <Button
                        size="sm"
                        disabled={minutesMut.isPending}
                        onClick={() => minutesMut.mutate({ id: detailQ.data!.meeting.id, minutes: minutesDraft })}
                      >
                        Save minutes
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-vt-12 text-vt-text-muted">Minutes can be captured once the meeting starts.</p>
                )}
              </section>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </PageHeader>
  );
}

function KpiCard({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }): JSX.Element {
  return (
    <Card>
      <CardContent className="flex items-center gap-vt-4 p-vt-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">{icon}</div>
        <div>
          <div className="text-vt-12 text-vt-text-muted">{label}</div>
          <div className="text-vt-20 font-semibold text-vt-text">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-vt-sm bg-vt-surface-2 p-2">
      <div className="text-vt-text-muted">{label}</div>
      <div className="font-semibold text-vt-text">{value}</div>
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  variant
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'danger';
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'block w-full rounded-vt-sm px-3 py-1.5 text-left text-vt-13 transition-colors ' +
        (variant === 'danger'
          ? 'text-vt-danger hover:bg-vt-danger-50'
          : 'text-vt-text hover:bg-vt-surface-2')
      }
    >
      {children}
    </button>
  );
}
